import { useQuery } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { InspectionIcon } from '@/components/entity-icons';
import { tabIntoForm } from '@/lib/utils/focus';
import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router';

import type { WithDeletedBy } from '@/common/soft-delete';
import type { InspectionType } from '@/common/inspection';
import { DamageList } from '@/components/damage/damage-table/damage-list';
import { DeletedBadge } from '@/components/soft-delete/DeletedInfo';
import { DeletedItemBanner } from '@/components/soft-delete/DeletedItemBanner';
import { RestoreItemButton } from '@/components/soft-delete/RestoreItemButton';
import { ReadOnlySubtree } from '@/context/AssetReadOnlyContext';
import { useDeletedPreview } from '@/hooks/use-deleted-preview';
import { softDeleteTargets } from '@/queries/soft-delete/soft-delete';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { getInspectionOptions } from '@/queries/inspections/get-inspection';
import { ROLE } from '../../../common/role';
import { usePermission } from '../../../hooks/use-permission';
import { DataUploadListContainer } from '../../data-upload/data-upload-list/page';
import { Input } from '../../ui/input';
import { InspectionOverview } from './overview';

export function InspectionDetail() {
	const { t } = useTranslation();
	const { inspectionId, assetId } = useParams<{ inspectionId: string; assetId: string }>();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const tabFromUrl = searchParams.get('tab') ?? 'overview';
	const isNewInspection = inspectionId === 'new';
	const [inspectionTitle, setInspectionTitle] = useState<string>(
		isNewInspection ? t('inspections.detail.newTitle') : ''
	);
	const titleInputRef = useRef<HTMLInputElement>(null);

	// A new inspection starts with the cursor in its title
	useEffect(() => {
		if (isNewInspection) titleInputRef.current?.select();
	}, [isNewInspection]);
	const shouldReadDamages = usePermission(ROLE.DAMAGES_READ);
	const shouldReadDataUploads = usePermission(ROLE.DATA_UPLOADS_READ);
	// A deleted inspection opened from the deleted list: read-only, overview only (GH #331)
	const isDeleted = useDeletedPreview('inspection');
	const shouldUpdateInspections = usePermission(ROLE.INSPECTIONS_UPDATE) && !isDeleted;

	const { data: inspectionDetailResponse, isLoading } = useQuery(
		getInspectionOptions({
			assetId: assetId as string,
			id: inspectionId as string,
			enabled: !isNewInspection,
			deleted: isDeleted,
		})
	);

	const inspection = (inspectionDetailResponse || null) as WithDeletedBy<InspectionType> | null;

	const [activeTab, setActiveTab] = useState(tabFromUrl);

	console.log('Inspection Detail Render:', { asset: inspection, isLoading });

	useEffect(() => {
		if (inspection?.title) {
			setInspectionTitle(inspection.title);
		}
	}, [inspection?.title]);

	if (isLoading || !assetId) return <div className="p-6">{t('common.states.loading')}</div>;

	if (!isLoading && !inspection && inspectionId !== 'new')
		return <div className="p-6">{t('inspections.detail.notFound')}</div>;

	const tabList = [
		{ value: 'overview', label: t('inspections.detail.tabs.overview') },
		{
			value: 'damages',
			label: t('inspections.detail.tabs.damages', { count: inspection?.damagesCount || 0 }),
			disabled: isNewInspection,
			hidden: !shouldReadDamages,
		},
		{
			value: 'data_upload',
			label: t('inspections.detail.tabs.dataUpload', { count: inspection?.filesCount || 0 }),
			disabled: isNewInspection,
			hidden: !shouldReadDataUploads,
		},
		// Its damages and files were deleted with it; the restore dialog says what comes back
	].filter((tab) => !isDeleted || tab.value === 'overview');

	const onInspectionTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setInspectionTitle(e.target.value);
	};

	return (
		<ReadOnlySubtree active={isDeleted}>
			<div className="border-b text-card-foreground bg-card flex-none">
				<div className="p-6 pb-4">
					<Button
						variant="ghost"
						className="text-muted-foreground hover:text-primary hover:bg-transparent! px-0! mb-2 gap-0 hover:pl-2! cursor-pointer"
						onClick={() => navigate(`/assets/${assetId}?tab=inspections`)}
					>
						<ArrowLeft className="size-4 mr-2" />
						{t('inspections.detail.backToAsset')}
					</Button>
					<div className="flex items-center gap-2">
						<InspectionIcon
							className="size-5 md:size-6 shrink-0 text-muted-foreground"
							aria-label={t('common.entities.inspection', { count: 1 })}
						/>
						<Input
							ref={titleInputRef}
							type="text"
							onChange={onInspectionTitleChange}
							onKeyDown={tabIntoForm}
							value={inspectionTitle}
							disabled={!shouldUpdateInspections}
							className="border-transparent hover:border-foreground/20 dark:bg-transparent text-lg md:text-2xl font-bold px-2 py-1 h-auto w-full shadow-none"
						/>
						{isDeleted && <DeletedBadge />}
					</div>
				</div>

				{isDeleted && inspection && (
					<DeletedItemBanner
						className="mx-6 mb-3"
						itemLabel={t('common.entities.inspection', { count: 1 })}
						deletedAt={inspection.deletedAt}
						deletedBy={inspection.deletedBy}
					/>
				)}
				<Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col flex-1">
					<TabsList className="w-full justify-start rounded-none border-b border-transparent bg-transparent p-0">
						{tabList.map((tab) => (
							<TabsTrigger
								key={tab.value}
								className="rounded-none border-0 border-b-2 border-transparent data-[state=active]:border-primary! data-[state=active]:bg-transparent data-[state=active]:text-primary! bg-transparent! text-muted-foreground hover:text-primary! hover:cursor-pointer px-4 py-2"
								value={tab.value}
								disabled={tab.disabled}
								hidden={tab.hidden}
							>
								{tab.label}
							</TabsTrigger>
						))}
					</TabsList>
				</Tabs>
			</div>
			<div className="flex-1 overflow-auto bg-background text-foreground p-4">
				{(activeTab === 'overview' || isDeleted) && (
					<InspectionOverview
						inspection={inspection}
						isNew={isNewInspection}
						assetId={assetId}
						title={inspectionTitle}
						restoreButton={
							isDeleted && inspection ? (
								<RestoreItemButton
									target={softDeleteTargets.inspection(assetId, inspectionId as string)}
									name={inspection.title || t('common.entities.inspection', { count: 1 })}
									restoreRole={ROLE.INSPECTIONS_DELETE}
									onRestored={() => navigate(`/assets/${assetId}/inspections/${inspectionId}`, { replace: true })}
									className="h-7 lg:h-9 text-xs lg:text-sm px-2 lg:px-3"
								/>
							) : undefined
						}
					/>
				)}
				{!isDeleted && activeTab === 'damages' && (
					<DamageList assetId={assetId} inspectionId={inspectionId as string} />
				)}
				{!isDeleted && activeTab === 'data_upload' && (
					<DataUploadListContainer
						type="inspection"
						inspectionId={inspectionId}
						assetId={assetId}
					/>
				)}
			</div>
		</ReadOnlySubtree>
	);
}
